import { Button } from "@procurement-agents/ui";
import type { AttachmentMeta, AttachmentRef, ConfirmPrefillPayload, ConfirmPrefillResume, IntakeQuestionPayload, IntakeReviewPayload, IntakeReviewResume, IntakeTurn, ReplyWithAttachments } from "@procurement-agents/scope-api";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { AttachmentPicker, TurnAttachments, type Uploader } from "./Attachments";
import { ConfirmPrefill, type DraftValues } from "./ConfirmPrefill";
import { IntakeReview } from "./IntakeReview";
import { useHandsFree, useStreamedSpeech, VoiceControls } from "../handsFree";
import { Heard, MicButton, MicMessage, insertAt, useIntakeVoice, useMicMessage, type IntakeVoice } from "../voice";

export type ChatPending = { kind: "question"; payload: IntakeQuestionPayload } | { kind: "prefill"; payload: ConfirmPrefillPayload } | { kind: "review"; payload: IntakeReviewPayload } | { kind: "typing"; label?: string } | { kind: "streaming"; text: string; done: boolean } | { kind: "handoff" } | { kind: "waiting"; role: string } | null;
/** The user's turn shown before the server has recorded it; replaced in place by the matching server turn. */
export interface OptimisticTurn { id: string; text: string; question_id: string | null }

const NEAR_BOTTOM_PX = 48;
const PREFILL_LINE = "From your description I've captured these — anything wrong?";
const HANDOFF_LINE = "I have what I need — the procurement team will take it from here.";
const TEXTAREA_CAP_PX = 180;

/** Stable key per turn (UX section 3): ts + role + question_id, falling back to position for turns without a ts. */
function keysFor(turns: IntakeTurn[]): string[] {
  const seen = new Map<string, number>();
  return turns.map((t, i) => {
    const base = `${t.ts ?? `#${i}`}|${t.role}|${t.question_id ?? ""}`;
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n ? `${base}|${n}` : base;
  });
}

/** Intake as a conversation (UX section 3). The pending question is the composer, not a form. The transcript never
 * moves under the user: stable keys, a reserved slot for the typing indicator, a fixed-height dock the textarea
 * grows inside, auto-scroll only when they were already at the bottom, and a composer that is disabled while the
 * agent replies rather than unmounted. */
export function IntakeChat({ transcript, pending, opening, onSubmit, busy, currency, optimistic, upload, onOpenAttachment, onDownloadQuestions, draft, onSaveDraft, voiceAllowed = false }: {
  transcript: IntakeTurn[]; pending: ChatPending; opening?: string | null; onSubmit: (reply: string | ReplyWithAttachments | ConfirmPrefillResume | IntakeReviewResume) => void; busy: boolean; currency?: string; optimistic?: OptimisticTurn | null;
  upload?: Uploader; onOpenAttachment?: (a: AttachmentRef) => void; onDownloadQuestions?: () => Promise<void> | void;
  draft?: DraftValues | null; onSaveDraft?: (values: DraftValues) => Promise<unknown>;
  /** The Voice toggle, for the person the stop is waiting on (CHANGE_voice_level3 §14). */
  voiceAllowed?: boolean;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const onScroll = () => { const el = scroller.current; if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX; };
  const streamedChars = pending?.kind === "streaming" ? pending.text.length : 0;
  useLayoutEffect(() => { const el = scroller.current; if (el && atBottom.current) el.scrollTop = el.scrollHeight; }, [transcript.length, pending?.kind, optimistic?.id, streamedChars]);
  // the last question asked, so the composer stays mounted (disabled) while the agent replies
  const [lastQuestion, setLastQuestion] = useState<IntakeQuestionPayload | null>(pending?.kind === "question" ? pending.payload : null);
  useEffect(() => { if (pending?.kind === "question") setLastQuestion(pending.payload); }, [pending]);
  const showOpening = opening && !transcript.some((t) => t.content === opening);
  const showOptimistic = !!optimistic && !transcript.some((t) => t.role === "user" && t.question_id === optimistic.question_id && t.content === optimistic.text);
  const keys = keysFor(transcript);
  // The review card invites a typed correction, so the composer has to be there to take one. It is a plain text
  // box: the answer goes back as a string and the server reads it against the card.
  const reviewQ: IntakeQuestionPayload | null = pending?.kind === "review"
    // required: there is no Skip here — "nothing to change" is the card's own button, and an empty correction
    // would only say the same thing less clearly
    ? { type: "intake_question", question_id: "intake_review", prompt: pending.payload.prompt, answer_type: "text", required: true, is_follow_up: false }
    : null;
  const composerQ = reviewQ ?? (pending?.kind === "question" ? pending.payload : pending?.kind === "typing" || pending?.kind === "streaming" ? lastQuestion : null);
  // The question the user is answering stays on screen while the agent works out the next one. It lives in
  // `pending` until the server writes the transcript turn, so dropping it leaves the answer hanging under nothing.
  const askedNow = pending?.kind === "question" ? pending.payload.prompt
    : pending?.kind === "typing" && lastQuestion && !transcript.some((t) => t.role === "agent" && t.content === lastQuestion.prompt) ? lastQuestion.prompt
    : null;
  const dockEmpty = !composerQ && pending?.kind !== "typing" && pending?.kind !== "streaming" && pending?.kind !== "handoff";
  // Voice (CHANGE_voice_level3 §14): the toggle is the hands-free conversation. A question streaming in is spoken
  // sentence by sentence as it arrives; the page's run turn (RunView) says the rest and listens.
  const voice = useIntakeVoice();
  const hf = useHandsFree();
  useStreamedSpeech(voiceAllowed && pending?.kind === "streaming" ? pending : null);
  return (
    <div className={`chat-shell${pending?.kind === "prefill" || pending?.kind === "review" ? " expanded" : ""}${hf.active ? " voice-on" : ""}`}>
    {voiceAllowed && <VoiceControls />}
    <div className="chat-scroll" ref={scroller} onScroll={onScroll}>
    <div className="chat">
      {showOpening && <div className="turn agent opening">{opening}</div>}
      {transcript.map((t, i) => {
        // a skipped question is recorded with an empty reply: said so, not drawn as an empty bubble
        const skipped = t.role === "user" && !t.content.trim() && !t.attachments?.length;
        return <div key={keys[i]} className={`turn ${t.role}${skipped ? " skipped" : ""}`}>{skipped ? "Skipped" : t.content}<TurnAttachments items={t.attachments} onOpen={onOpenAttachment} /></div>;
      })}
      {askedNow && <div className="turn agent">{askedNow}</div>}
      {showOptimistic && optimistic && <div key={`opt-${optimistic.id}`} className={`turn user optimistic${optimistic.text.trim() ? "" : " skipped"}`}>{optimistic.text.trim() ? optimistic.text : "Skipped"}</div>}
      {pending?.kind === "prefill" && <div className="turn agent">{PREFILL_LINE}</div>}
      {pending?.kind === "streaming" && <div className="turn agent streaming" aria-live="polite">{pending.text}{!pending.done && <span className="caret" aria-hidden />}</div>}
      {pending?.kind === "handoff" && <div className="turn agent">{HANDOFF_LINE}</div>}
      {pending?.kind === "waiting" && <p className="muted small status-line">Waiting on {pending.role}.</p>}
      {pending?.kind === "review" && <IntakeReview payload={pending.payload} onSubmit={onSubmit} busy={busy} />}
      {pending?.kind === "prefill" && <ConfirmPrefill payload={pending.payload} onSubmit={onSubmit} busy={busy} onDownloadQuestions={onDownloadQuestions} currency={currency} draft={draft} onSaveDraft={onSaveDraft} />}
      {/* reserved slot: the typing indicator appears here without changing the layout above it */}
      <div className="turn-slot" aria-live="polite">
        {pending?.kind === "typing" && <div className="turn agent typing" aria-label="The agent is working"><span className="dots"><span /><span /><span /></span>{pending.label && <span className="muted small">{pending.label}</span>}</div>}
      </div>
    </div>
    </div>
    <div className={`chat-dock${dockEmpty ? " empty" : ""}`}>
      {composerQ && <Composer key={reviewQ ? "review" : composerQ.answer_type} q={composerQ} onSubmit={onSubmit} busy={busy} disabled={pending?.kind !== "question" && pending?.kind !== "review"} currency={currency} upload={reviewQ ? undefined : upload} voice={voice} />}
      {!composerQ && (pending?.kind === "typing" || pending?.kind === "streaming") && <div className="composer"><textarea rows={2} disabled placeholder="The agent is replying…" aria-label="Your answer (waiting)" /></div>}
      {pending?.kind === "handoff" && <p className="muted small status-line">The composer is closed. You will be able to read the scope of work here once it is complete.</p>}
    </div>
    </div>
  );
}

function Composer({ q, onSubmit: submitRaw, busy, disabled = false, currency, upload, voice }: { q: IntakeQuestionPayload; onSubmit: (reply: string | ReplyWithAttachments) => void; busy: boolean; disabled?: boolean; currency?: string; upload?: Uploader; voice?: IntakeVoice }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState<AttachmentMeta[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [height, setHeight] = useState<number | null>(null);   // the textarea's grown height, held across re-renders
  const area = useRef<HTMLTextAreaElement>(null);
  const line = useRef<HTMLInputElement>(null);
  // what dictation heard for this answer; while set, the answer goes back marked `via: "voice"`
  const [heard, setHeard] = useState<string | null>(null);
  const [micMessage, setMicMessage, dismissMicMessage] = useMicMessage();
  const off = busy || disabled;
  // a new question resets what was typed for the previous one; the composer itself stays mounted
  useEffect(() => { setText(""); setFiles([]); setPicked([]); setFilter(""); setHeight(null); setHeard(null); }, [q.question_id, q.prompt]);
  useEffect(() => { if (!disabled) area.current?.focus(); }, [disabled, q.question_id, q.prompt]);
  // text questions may carry documents; the reply becomes {text, attachment_ids} when any are attached
  const onSubmit = (reply: string, spoken: boolean) => {
    const via = spoken ? { via: "voice" as const } : {};
    if (files.length) submitRaw({ text: reply, attachment_ids: files.map((f) => f.attachment_id), ...via });
    else submitRaw(spoken ? { text: reply, ...via } : reply);
  };
  const attachable = q.answer_type === "text" && !!upload;
  const rows = useMemo(() => q.reference?.rows ?? [], [q.reference]);
  const keyField = q.reference?.key_field;
  const context = (choice: string) => {
    const row = keyField ? rows.find((r) => String(r[keyField]) === choice) : undefined;
    if (!row) return "";
    const first = Object.entries(row).find(([k, v]) => k !== keyField && typeof v === "string" && v);
    return first ? String(first[1]) : "";
  };
  // Clearing on send, not on the next question: the box is only emptied when the question changes, so a reply sat
  // in it for as long as the agent took to think, reading as though nothing had been sent.
  const submit = (value: string, spoken = false) => {
    if (off) return;
    voice?.interrupt();
    onSubmit(value, spoken && !!value);
    setText("");
    setFiles([]);
    setHeight(null);
    setHeard(null);
  };
  // emptied by hand, the box no longer holds what was heard: the "Heard" line and the voice mark go with it
  const typing = (value: string) => { voice?.interrupt(); setText(value); if (!value.trim()) setHeard(null); };
  // Dictation goes into the field to be checked, never straight out. For chips the server reads the words against
  // the options exactly as it reads typed text, so the UI does not guess; "Send" sends what was heard.
  const chips = q.answer_type === "boolean" || q.answer_type === "choice" || q.answer_type === "multi_choice";
  const dictated = (words: string) => {
    setHeard((h) => (h ? `${h} ${words}` : words));
    if (chips) return;
    const el = q.answer_type === "number" || q.answer_type === "date" ? line.current : area.current;
    const next = insertAt(text, words, el);
    setText(next);
    if (el instanceof HTMLTextAreaElement) requestAnimationFrame(() => grow(el));
  };
  const mic = voice?.available ? <MicButton voice={voice} onText={dictated} disabled={off} onMessage={setMicMessage} /> : null;
  const micLine = <MicMessage message={micMessage} onDismiss={dismissMicMessage} />;
  const heardChips = chips && heard ? (
    <div className="heard-row"><Heard text={heard} /><Button variant="primary" type="button" onClick={() => submit(heard, true)} disabled={off}>Send</Button><button type="button" className="link small" onClick={() => setHeard(null)}>Clear</button></div>
  ) : null;
  const isOpening = q.question_id === "request";
  // an optional question may be skipped; a required one may be answered "not known yet", which is recorded as to be
  // confirmed (shown at the review, an open question on the requisition) instead of being asked for again
  const deferrable = q.required && !q.confirms && !isOpening && q.question_id !== "intake_review";
  const skip = !q.required ? <button type="button" className="link" onClick={() => submit("")} disabled={off}>Skip</button>
    : deferrable ? <button type="button" className="link" onClick={() => submit("Not known yet")} disabled={off}>Not known yet</button> : null;
  const grow = (el: HTMLTextAreaElement) => { el.style.height = "auto"; const h = Math.min(el.scrollHeight, TEXTAREA_CAP_PX); el.style.height = `${h}px`; setHeight(h); };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter") return;
    const sendCombo = isOpening ? (e.metaKey || e.ctrlKey) : !e.shiftKey;
    if (sendCombo) { e.preventDefault(); if (text.trim() || files.length) submit(text.trim(), !!heard); }
  };

  if (q.answer_type === "boolean") return (
    <div className="composer chips"><button className="chip-btn" onClick={() => submit("yes")} disabled={off}>Yes</button><button className="chip-btn" onClick={() => submit("no")} disabled={off}>No</button>{skip}{mic}{heardChips}{micLine}</div>
  );
  if (q.answer_type === "choice") {
    // the agent's best guess first, marked; the rest follow in the customer's own order
    const ordered = q.suggested ? [q.suggested, ...(q.choices ?? []).filter((c) => c !== q.suggested)] : (q.choices ?? []);
    return (
      <div className="composer chips">
        {ordered.map((c) => (
          <button key={c} className={`chip-btn${c === q.suggested ? " suggested" : ""}`} onClick={() => submit(c)} disabled={off}>
            {c === q.suggested && <span className="tick" aria-hidden>✓ </span>}{c}{context(c) && <span className="muted"> · {context(c)}</span>}
          </button>
        ))}
        {skip}{mic}{heardChips}{micLine}
      </div>
    );
  }
  if (q.answer_type === "multi_choice") {
    const options = (q.choices ?? []).filter((c) => !picked.includes(c) && (c + " " + context(c)).toLowerCase().includes(filter.toLowerCase()));
    return (
      <form className="composer picker" onSubmit={(e) => { e.preventDefault(); if (picked.length) submit(picked.join(", ")); }}>
        <div className="picked">{picked.map((c) => <span key={c} className="chip removable">{c}<button type="button" aria-label={`Remove ${c}`} onClick={() => setPicked((p) => p.filter((x) => x !== c))} disabled={off}>×</button></span>)}</div>
        <span className="row">
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`Search ${q.reference?.display_name ?? "options"}…`} aria-label="Search options" disabled={off} />
          <button type="button" className="link small" onClick={() => setPicked(q.choices ?? [])} disabled={off || picked.length === (q.choices ?? []).length}>All {q.reference?.display_name?.toLowerCase() ?? "of them"}</button>
        </span>
        <ul className="options" role="listbox">{options.map((c) => <li key={c} role="option" aria-selected={false} onClick={() => { if (!off) { setPicked((p) => [...p, c]); setFilter(""); } }}>{c}{context(c) && <span className="muted small"> · {context(c)}</span>}</li>)}</ul>
        <div className="row"><Button variant="primary" type="submit" disabled={off || !picked.length}>Send</Button>{skip}{mic}</div>
        {heardChips}{micLine}
      </form>
    );
  }
  const field = q.answer_type === "number" ? (
    <div className="prefixed"><span className="prefix">{currency ?? ""}</span><input ref={line} inputMode="decimal" value={text} onChange={(e) => typing(e.target.value)} placeholder="2,100,000 or 2.1m" autoFocus aria-label="Amount" disabled={off} onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) { e.preventDefault(); submit(text.trim(), !!heard); } }} /></div>
  ) : q.answer_type === "date" ? (
    <input ref={line} value={text} onChange={(e) => typing(e.target.value)} placeholder="1 July 2027, July 2027, Q3 2027…" autoFocus aria-label="Date" disabled={off} onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) { e.preventDefault(); submit(text.trim(), !!heard); } }} />
  ) : (
    <textarea ref={area} value={text} onChange={(e) => { typing(e.target.value); grow(e.target); }} onKeyDown={onKey} rows={isOpening ? 5 : 2} style={height ? { height } : undefined} disabled={disabled}
      placeholder={disabled ? "The agent is replying…" : isOpening ? "Describe what you are planning to procure. Cmd/Ctrl+Enter to send." : "Type your answer. Enter to send, Shift+Enter for a new line."} autoFocus aria-label="Your answer" />
  );
  const canSend = !!text.trim() || files.length > 0;
  const actions = <>{mic}{skip}<Button variant="primary" type="submit" form="intake-composer" disabled={off || !canSend}>Send</Button></>;
  const form = (
    <form id="intake-composer" className="composer" onSubmit={(e) => { e.preventDefault(); if (canSend) submit(text.trim(), !!heard); }}>
      {q.confirms && (
        // a read-back: one click to agree or not, or the correction in the box
        <div className="chips confirm-chips">
          <button type="button" className="chip-btn" onClick={() => submit("yes")} disabled={off}>Yes</button>
          <button type="button" className="chip-btn" onClick={() => submit("no")} disabled={off}>No</button>
        </div>
      )}
      {field}
      <Heard text={heard} />
      {micLine}
      {!attachable && <div className="composer-actions">{actions}</div>}
    </form>
  );
  // with documents allowed the action row is shared: attach and chips on the left, skip and send on the right
  return attachable ? <AttachmentPicker upload={upload!} items={files} onChange={setFiles} disabled={off} actions={actions}>{form}</AttachmentPicker> : form;
}
